import {
  boolean,
  index,
  int,
  mysqlTable,
  text,
  timestamp,
  uniqueIndex,
  varchar,
} from "drizzle-orm/mysql-core";

/**
 * One row per reminder actually attempted for a customer. UNIQUE(appointmentId, startsAt)
 * is the send dedupe: the row is claimed before Twilio is called, so overlapping passes or
 * instances cannot double-send. A changed start time is a new key (new reminder allowed).
 * Dry-run never writes here.
 */
export const appointmentReminderSends = mysqlTable(
  "appointment_reminder_sends",
  {
    id: int("id").autoincrement().primaryKey(),
    appointmentId: int("appointmentId").notNull(),
    startsAt: timestamp("startsAt").notNull(),
    customerId: int("customerId"),
    salespersonUserId: int("salespersonUserId"),
    salespersonName: varchar("salespersonName", { length: 255 }),
    phoneE164: varchar("phoneE164", { length: 32 }).notNull(),
    /** claimed -> submitted | failed */
    status: varchar("status", { length: 32 }).notNull().default("claimed"),
    trigger: varchar("trigger", { length: 32 }).notNull(),
    contentSid: varchar("contentSid", { length: 64 }),
    twilioSid: varchar("twilioSid", { length: 64 }),
    attempts: int("attempts").notNull().default(1),
    lastError: text("lastError"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  (t) => ({
    apptStartUnique: uniqueIndex("appointment_reminder_sends_appt_start_unique").on(t.appointmentId, t.startsAt),
    phoneIdx: index("appointment_reminder_sends_phone_idx").on(t.phoneE164, t.createdAt),
    twilioSidIdx: index("appointment_reminder_sends_twilio_sid_idx").on(t.twilioSid),
  }),
);

/**
 * Audit trail: every send, dry-run, skip, failure, nudge, reschedule, opt-out/in and template
 * submission step. dedupeKey (nullable, unique) keeps per-day skip/nudge rows to one each
 * and doubles as the once-only marker for the template submission boot step.
 */
export const appointmentReminderEvents = mysqlTable(
  "appointment_reminder_events",
  {
    id: int("id").autoincrement().primaryKey(),
    appointmentId: int("appointmentId"),
    startsAt: timestamp("startsAt"),
    customerId: int("customerId"),
    salespersonUserId: int("salespersonUserId"),
    sendId: int("sendId"),
    action: varchar("action", { length: 48 }).notNull(),
    reason: varchar("reason", { length: 128 }),
    dryRun: boolean("dryRun").notNull().default(false),
    dedupeKey: varchar("dedupeKey", { length: 191 }),
    metadataJson: text("metadataJson"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  (t) => ({
    dedupeUnique: uniqueIndex("appointment_reminder_events_dedupe_unique").on(t.dedupeKey),
    apptIdx: index("appointment_reminder_events_appt_idx").on(t.appointmentId, t.createdAt),
    actionIdx: index("appointment_reminder_events_action_idx").on(t.action, t.createdAt),
  }),
);

/** Customer WhatsApp opt-outs (STOP / UNSUBSCRIBE). optedInAt set when they reply START. */
export const whatsappOptOuts = mysqlTable(
  "whatsapp_opt_outs",
  {
    id: int("id").autoincrement().primaryKey(),
    phoneE164: varchar("phoneE164", { length: 32 }).notNull(),
    customerId: int("customerId"),
    optedOut: boolean("optedOut").notNull().default(true),
    optedOutAt: timestamp("optedOutAt"),
    optedInAt: timestamp("optedInAt"),
    source: varchar("source", { length: 64 }),
    lastKeyword: varchar("lastKeyword", { length: 32 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  (t) => ({
    phoneUnique: uniqueIndex("whatsapp_opt_outs_phone_unique").on(t.phoneE164),
  }),
);

export type AppointmentReminderSend = typeof appointmentReminderSends.$inferSelect;
export type AppointmentReminderEvent = typeof appointmentReminderEvents.$inferSelect;
export type WhatsappOptOut = typeof whatsappOptOuts.$inferSelect;
